import { ChartSkeleton, Card } from '@blockanalitica/ui';
import { useMemo, useState } from 'react';
import { useApi } from '@/api';
import type { TooltipItem, ChartTypeRegistry } from 'chart.js';
import { ChartTimeFilter } from '@/components';
import TreasuryChart from './TreasuryChart';
import { useTheme } from 'styled-components';

export type TreasuryData = {
  date: string;
  underlying_address: string;
  underlying_symbol: string;
  treasury: string;
};

export default function HistoricGraphComponent() {
  const theme = useTheme();
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [data, error] = useApi<TreasuryData[]>({
    url: '/treasury/historic/',
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const chartConfig = useMemo(
    () => ({
      options: {
        // @ts-ignore
        scales: {
          y: {
            stacked: true
          }
        },
        aspectRatio: 3,
        plugins: {
          legend: {
            display: true,
            position: 'bottom' as const,
            labels: {
              color: theme.colors.muted,
              boxWidth: 20
            }
          },
          tooltip: {
            itemSort: (
              a: TooltipItem<keyof ChartTypeRegistry>,
              b: TooltipItem<keyof ChartTypeRegistry>
            ) => {
              const ay = (a.parsed as { y?: number | null }).y ?? 0;
              const by = (b.parsed as { y?: number | null }).y ?? 0;
              return by - ay;
            }
          }
        }
      }
    }),
    [theme.colors.muted]
  );

  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );
  if (!data) {
    return <ChartSkeleton />;
  }
  return (
    <Card>
      <TreasuryChart
        data={data}
        timeFilter={timeFilter}
        config={chartConfig}
        chartDaysAgo={chartDaysAgo}
      />
    </Card>
  );
}
